import React, { useState, useEffect } from 'react';

const API_BASE_URL = 'https://www.lankastudio.cn/api';

function Tutorials() {
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [playingVideo, setPlayingVideo] = useState(null);
  const [tutorials, setTutorials] = useState([]);
  const [categories, setCategories] = useState(['全部']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 加载教程数据
  useEffect(() => {
    fetchTutorials();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const fetchTutorials = async () => {
    try {
      setLoading(true);
      let url = `${API_BASE_URL}/tutorials?`;
      
      if (selectedCategory !== '全部') {
        url += `category=${encodeURIComponent(selectedCategory)}`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error('获取教程失败');
      
      const data = await response.json();
      setTutorials(data.tutorials || []);
      setError(null);
    } catch (err) {
      console.error('获取教程失败:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tutorials/categories/list`);
      if (!response.ok) return;
      
      const data = await response.json();
      if (data.categories && data.categories.length > 0) {
        setCategories(['全部', ...data.categories]);
      }
    } catch (err) {
      console.error('获取分类失败:', err);
    }
  };

  // 过滤教程（前端二次过滤，确保分类正确）
  const filteredTutorials = selectedCategory === '全部' 
    ? tutorials 
    : tutorials.filter(t => t.category === selectedCategory);

  return (
    <div style={styles.container}>
      {/* 背景装饰 */}
      <div style={styles.bgDecoration}></div>
      <div style={styles.bgDecoration2}></div>

      <div style={styles.content}>
        {/* 页面标题 */}
        <div style={styles.header}>
          <h1 style={styles.pageTitle}>
            <span style={styles.titleIcon}>📚</span>
            教程学习
          </h1>
          <p style={styles.pageSubtitle}>从入门到精通，系统学习模型制作技巧</p>
        </div>

        {/* 分类标签 */}
        <div style={styles.categoryBar}>
          {categories.map(cat => (
            <button
              key={cat}
              style={{
                ...styles.categoryTag,
                backgroundColor: selectedCategory === cat ? '#12B7F5' : 'transparent',
                borderColor: selectedCategory === cat ? '#12B7F5' : '#3d4450',
                color: selectedCategory === cat ? '#fff' : '#9ca3af',
              }}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* 加载状态 */}
        {loading ? (
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>加载教程中...</p>
          </div>
        ) : error ? (
          <div style={styles.errorState}>
            <span style={styles.errorIcon}>❌</span>
            <p style={styles.errorText}>{error}</p>
            <button style={styles.retryBtn} onClick={fetchTutorials}>重试</button>
          </div>
        ) : filteredTutorials.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>📭</span>
            <p style={styles.emptyText}>暂无相关教程</p>
          </div>
        ) : (
          /* 视频网格 */
          <div style={styles.videoGrid}>
            {filteredTutorials.map((tutorial, index) => (
              <VideoCard 
                key={tutorial.id}
                tutorial={tutorial}
                index={index}
                onPlay={() => setPlayingVideo(tutorial)}
              />
            ))}
          </div>
        )}

        {/* 更多提示 */}
        <div style={styles.moreHint}>
          <p>🎬 更多精彩教程持续更新中...</p>
          <a 
            href="https://space.bilibili.com" 
            target="_blank" 
            rel="noopener noreferrer"
            style={styles.biliLink}
          >
            访问B站获取更多内容 →
          </a>
        </div>
      </div>

      {/* 视频播放器弹窗 */}
      {playingVideo && (
        <VideoPlayer 
          video={playingVideo} 
          onClose={() => setPlayingVideo(null)} 
        />
      )}
    </div>
  );
}

// 视频卡片组件 - 使用B站嵌入播放器显示缩略图
function VideoCard({ tutorial, onPlay, index }) {
  const [isHovered, setIsHovered] = useState(false);

  // 使用数据库中的嵌入链接，添加autoplay=0参数（如果还没有）
  const embedUrl = tutorial.embed_url 
    ? (tutorial.embed_url.includes('autoplay=') 
        ? tutorial.embed_url.replace(/autoplay=\d+/, 'autoplay=0')
        : tutorial.embed_url + (tutorial.embed_url.includes('?') ? '&' : '?') + 'autoplay=0')
    : '';

  return (
    <div 
      style={{
        ...styles.videoCard,
        transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0)',
        boxShadow: isHovered 
          ? '0 20px 40px rgba(18, 183, 245, 0.2)' 
          : '0 4px 20px rgba(0, 0, 0, 0.3)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* 视频封面 - 使用B站嵌入播放器 */}
      <div style={styles.videoThumbnail}>
        {embedUrl ? (
          <iframe
            src={embedUrl}
            style={styles.thumbnailIframe}
            scrolling="no"
            border="0"
            frameBorder="no"
            framespacing="0"
            allowFullScreen={true}
            title={tutorial.title}
          />
        ) : (
          <div style={styles.placeholderThumbnail}>
            <span style={styles.placeholderText}>暂无视频</span>
          </div>
        )}
        
        {/* 点击遮罩层 - 用于触发大窗口播放 */}
        <div 
          style={{
            ...styles.clickOverlay,
            opacity: isHovered ? 1 : 0,
          }}
          onClick={onPlay}
        >
          <div style={styles.expandButton}>
            <span style={styles.expandIcon}>⛶</span>
            <span>大窗口播放</span>
          </div>
        </div>
        
        {/* 难度标签 */}
        <div style={{
          ...styles.difficultyTag,
          backgroundColor: tutorial.difficulty === '入门' ? 'rgba(16, 185, 129, 0.9)' : 'rgba(245, 158, 11, 0.9)',
        }}>
          {tutorial.difficulty}
        </div>
      </div>

      {/* 视频信息 */}
      <div style={styles.videoInfo}>
        <h3 style={styles.videoTitle}>{tutorial.title}</h3>
        <div style={styles.videoMeta}>
          <span style={styles.categoryLabel}>
            <BilibiliIcon /> {tutorial.category}
          </span>
        </div>
      </div>
    </div>
  );
}

// B站图标组件
function BilibiliIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="#00A1D6">
      <path d="M17.813 4.653h.854c1.51.054 2.769.578 3.773 1.574 1.004.995 1.524 2.249 1.56 3.76v7.36c-.036 1.51-.556 2.769-1.56 3.773s-2.262 1.524-3.773 1.56H5.333c-1.51-.036-2.769-.556-3.773-1.56S.036 18.858 0 17.347v-7.36c.036-1.511.556-2.765 1.56-3.76 1.004-.996 2.262-1.52 3.773-1.574h.774l-1.174-1.12a1.234 1.234 0 0 1-.373-.906c0-.356.124-.659.373-.907l.027-.027c.267-.249.573-.373.92-.373.347 0 .653.124.92.373L9.653 4.44c.071.071.134.142.187.213h4.267a.836.836 0 0 1 .16-.213l2.853-2.747c.267-.249.573-.373.92-.373.347 0 .662.151.929.4.267.249.391.551.391.907 0 .355-.124.657-.373.906zM5.333 7.24c-.746.018-1.373.276-1.88.773-.506.498-.769 1.13-.786 1.894v7.52c.017.764.28 1.395.786 1.893.507.498 1.134.756 1.88.773h13.334c.746-.017 1.373-.275 1.88-.773.506-.498.769-1.129.786-1.893v-7.52c-.017-.765-.28-1.396-.786-1.894-.507-.497-1.134-.755-1.88-.773zM8 11.107c.373 0 .684.124.933.373.25.249.383.569.4.96v1.173c-.017.391-.15.711-.4.96-.249.25-.56.374-.933.374s-.684-.125-.933-.374c-.25-.249-.383-.569-.4-.96V12.44c0-.373.129-.689.386-.947.258-.257.574-.386.947-.386zm8 0c.373 0 .684.124.933.373.25.249.383.569.4.96v1.173c-.017.391-.15.711-.4.96-.249.25-.56.374-.933.374s-.684-.125-.933-.374c-.25-.249-.383-.569-.4-.96V12.44c.017-.391.15-.711.4-.96.249-.249.56-.373.933-.373z"/>
    </svg>
  );
}

// 视频播放器组件
function VideoPlayer({ video, onClose }) {
  // 键盘关闭
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div style={styles.playerOverlay} onClick={onClose}>
      <div style={styles.playerContainer} onClick={(e) => e.stopPropagation()}>
        {/* 关闭按钮 */}
        <button style={styles.closeBtn} onClick={onClose}>✕</button>
        
        {/* 视频标题 */}
        <h2 style={styles.playerTitle}>{video.title}</h2>
        
        {/* B站嵌入播放器 - 直接使用数据库中的嵌入链接 */}
        {video.embed_url ? (
          <div style={styles.playerWrapper}>
            <iframe
              src={video.embed_url.includes('autoplay=') 
                ? video.embed_url.replace(/autoplay=\d+/, 'autoplay=1')
                : video.embed_url + (video.embed_url.includes('?') ? '&' : '?') + 'autoplay=1'
              }
              style={styles.playerIframe}
              scrolling="no"
              border="0"
              frameBorder="no"
              framespacing="0"
              allowFullScreen={true}
              title={video.title}
            />
          </div>
        ) : (
          <div style={styles.playerWrapper}>
            <div style={styles.noVideoPlaceholder}>
              <span>暂无视频链接</span>
            </div>
          </div>
        )}

        {/* 在B站观看 - 从embed_url中提取bvid */}
        {video.embed_url && (
          <a 
            href={(() => {
              const bvidMatch = video.embed_url.match(/bvid=([^&]+)/);
              return bvidMatch ? `https://www.bilibili.com/video/${bvidMatch[1]}` : '#';
            })()}
            target="_blank"
            rel="noopener noreferrer"
            style={styles.watchOnBili}
          >
            <BilibiliIcon /> 在B站观看完整视频
          </a>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '90vh',
    background: 'linear-gradient(180deg, #1a1d23 0%, #282c34 100%)',
    position: 'relative',
    overflow: 'hidden',
  },
  bgDecoration: {
    position: 'absolute',
    top: '-30%',
    left: '-10%',
    width: '50%',
    height: '80%',
    background: 'radial-gradient(circle, rgba(18, 183, 245, 0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  bgDecoration2: {
    position: 'absolute',
    bottom: '-20%',
    right: '-10%',
    width: '40%',
    height: '60%',
    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  content: {
    position: 'relative',
    zIndex: 10,
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  pageTitle: {
    fontSize: '42px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
  },
  titleIcon: {
    fontSize: '40px',
  },
  pageSubtitle: {
    fontSize: '18px',
    color: '#9ca3af',
  },
  categoryBar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '40px',
  },
  categoryTag: {
    padding: '10px 20px',
    borderRadius: '25px',
    border: '1px solid',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    background: 'transparent',
  },
  videoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  },
  videoCard: {
    backgroundColor: 'rgba(37, 40, 48, 0.9)',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  videoThumbnail: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%', // 16:9
    backgroundColor: '#1a1d23',
    overflow: 'hidden',
  },
  thumbnailIframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
  clickOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'opacity 0.3s ease',
    zIndex: 10,
  },
  expandButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    color: '#fff',
    fontSize: '14px',
    fontWeight: '500',
  },
  expandIcon: {
    fontSize: '32px',
  },
  difficultyTag: {
    position: 'absolute',
    top: '12px',
    left: '12px',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    color: '#fff',
  },
  playOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.3s ease',
  },
  playButton: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: 'rgba(18, 183, 245, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#fff',
    paddingLeft: '4px',
    transition: 'transform 0.3s ease',
    boxShadow: '0 4px 20px rgba(18, 183, 245, 0.4)',
  },
  biliTag: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    padding: '6px 10px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  videoInfo: {
    padding: '16px',
  },
  videoTitle: {
    color: '#fff',
    fontSize: '15px',
    fontWeight: '600',
    lineHeight: '1.5',
    marginBottom: '12px',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  videoMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  categoryLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#00A1D6',
    fontSize: '13px',
  },
  moreHint: {
    textAlign: 'center',
    marginTop: '60px',
    padding: '32px',
    backgroundColor: 'rgba(37, 40, 48, 0.5)',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },
  biliLink: {
    display: 'inline-block',
    marginTop: '12px',
    color: '#00A1D6',
    fontSize: '14px',
    textDecoration: 'none',
  },
  // 播放器样式
  playerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  playerContainer: {
    width: '100%',
    maxWidth: '1000px',
    backgroundColor: '#1a1d23',
    borderRadius: '16px',
    overflow: 'hidden',
    position: 'relative',
  },
  closeBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#fff',
    fontSize: '20px',
    cursor: 'pointer',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playerTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: '600',
    padding: '20px 60px 20px 20px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    margin: 0,
  },
  playerWrapper: {
    position: 'relative',
    width: '100%',
    paddingTop: '56.25%',
    backgroundColor: '#000',
  },
  playerIframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
  },
  watchOnBili: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '16px',
    color: '#00A1D6',
    fontSize: '14px',
    textDecoration: 'none',
    backgroundColor: 'rgba(0, 161, 214, 0.1)',
    transition: 'background 0.2s',
  },
  // 加载和错误状态
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(18, 183, 245, 0.2)',
    borderTop: '3px solid #12B7F5',
    borderRadius: '50%',
    margin: '0 auto 16px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#9ca3af',
    fontSize: '14px',
  },
  errorState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  errorIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  errorText: {
    color: '#ef4444',
    fontSize: '14px',
    marginBottom: '16px',
  },
  retryBtn: {
    padding: '10px 24px',
    backgroundColor: '#12B7F5',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
  },
  emptyIcon: {
    fontSize: '48px',
    display: 'block',
    marginBottom: '16px',
  },
  emptyText: {
    color: '#6b7280',
    fontSize: '16px',
  },
  placeholderThumbnail: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#1a1d23',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderText: {
    color: '#6b7280',
    fontSize: '14px',
  },
  noVideoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#000',
    color: '#fff',
    fontSize: '16px',
  },
};

export default Tutorials;

